// Typed REST client for the HTTP API. Admin calls attach the Cognito ID token.

import { loadConfig } from '../config';
import { currentIdToken } from '../auth/cognito';
import type { GameKind, MediaType, SideMedia, VoteChoice, VoteLabels } from '../shared/protocol';

export interface Topic {
  id: string;
  title: string;
  order: number;
  kind?: GameKind; // absent == quiz
  owner?: string;
  createdAt?: string;
}

export interface Question {
  id: string;
  topicId: string;
  order: number;
  text: string;
  points: number;
  answer?: string;
  mediaUrl?: string;
  mediaType?: MediaType;
  /** normcringe: the statement's right answer, if it has one */
  correct?: VoteChoice | '';
  /** normcringe: per-answer media */
  sides?: SideMedia;
}

/** Optional attachment on a question payload; both fields or neither. */
export interface QuestionMedia {
  mediaUrl?: string;
  mediaType?: MediaType;
}

export interface PresignedUpload {
  uploadUrl: string;
  contentType: string;
  mediaUrl: string;
  mediaType: MediaType;
}

export interface Game {
  id: string;
  code: string;
  status: string;
  kind?: GameKind; // absent == quiz
  windowSeconds?: number;
  voteLabels?: VoteLabels;
  donateUrl?: string;
  topicOrder: string[];
  createdBy?: string;
  createdAt: string;
  endedAt?: string;
}

/** Player-safe game info fetched by join code (no auth). */
export interface PublicGame {
  status: string;
  donateUrl?: string;
}

export interface Template {
  id: string;
  name: string;
  kind?: GameKind; // absent == quiz
  windowSeconds?: number; // absent/0 == default 30
  voteLabels?: VoteLabels; // normcringe only
  topicOrder: string[];
  owner?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Settings shared by a template and the games created from it. */
export interface LineupOptions {
  kind?: GameKind;
  windowSeconds?: number;
  voteLabels?: VoteLabels;
}

export interface LeaderEntry {
  nickname: string;
  totalScore: number;
  games: number;
}

export interface ImportReport {
  imported: number;
  skipped: number;
  reports: { title: string; imported: boolean; questions: number; error?: string }[];
}

async function request<T>(method: string, path: string, body?: unknown, auth = false): Promise<T> {
  const cfg = await loadConfig();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth) {
    const token = await currentIdToken();
    if (!token) throw new Error('Not signed in');
    headers['authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(cfg.httpApiUrl + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // Topics / questions (authoring — authenticated)
  listTopics: () => request<Topic[]>('GET', '/topics'),
  createTopic: (title: string, order = 0, kind: GameKind = 'quiz') =>
    request<Topic>('POST', '/topics', { title, order, kind }, true),
  updateTopic: (id: string, title: string, order: number, kind?: GameKind) =>
    request<Topic>('PUT', `/topics/${id}`, { title, order, kind }, true),
  deleteTopic: (id: string) => request<void>('DELETE', `/topics/${id}`, undefined, true),

  listQuestions: (topicId: string) => request<Question[]>('GET', `/topics/${topicId}/questions`, undefined, true),
  createQuestion: (topicId: string, q: { text: string; points: number; answer: string; correct?: VoteChoice | ''; sides?: SideMedia } & QuestionMedia) =>
    request<Question>('POST', `/topics/${topicId}/questions`, q, true),
  updateQuestion: (id: string, q: { text: string; points: number; answer: string; order?: number; correct?: VoteChoice | ''; sides?: SideMedia } & QuestionMedia) =>
    request<Question>('PUT', `/questions/${id}`, q, true),

  // Media: ask for a presigned PUT, then upload the bytes straight to S3.
  presignMedia: (contentType: string) => request<PresignedUpload>('POST', '/media/presign', { contentType }, true),
  deleteQuestion: (id: string) => request<void>('DELETE', `/questions/${id}`, undefined, true),

  importContent: (payload: unknown) => request<ImportReport>('POST', '/import', payload, true),

  // Templates (reusable topic lineups — authenticated)
  listTemplates: () => request<Template[]>('GET', '/templates', undefined, true),
  createTemplate: (name: string, topicOrder: string[], opts: LineupOptions = {}) =>
    request<Template>('POST', '/templates', { name, topicOrder, kind: 'quiz', ...opts }, true),
  updateTemplate: (id: string, name: string, topicOrder: string[], opts: LineupOptions = {}) =>
    request<Template>('PUT', `/templates/${id}`, { name, topicOrder, kind: 'quiz', ...opts }, true),
  deleteTemplate: (id: string) => request<void>('DELETE', `/templates/${id}`, undefined, true),

  // Games
  // kind is optional: the server derives it from the topics (and rejects mixed lineups).
  createGame: (topicOrder: string[], donateUrl?: string, opts: LineupOptions = {}) =>
    request<Game>('POST', '/games', { topicOrder, donateUrl, ...opts }, true),
  listGames: () => request<Game[]>('GET', '/games', undefined, true),
  // Public: fetch a game's player-safe info by join code (for the name-entry screen).
  gameByCode: (code: string) => request<PublicGame>('GET', `/games/by-code/${encodeURIComponent(code)}`),
  getGame: (id: string) =>
    request<{ game: Game; players: unknown[]; results: unknown[] }>('GET', `/games/${id}`, undefined, true),
  deleteGame: (id: string) => request<void>('DELETE', `/games/${id}`, undefined, true),

  leaderboard: () => request<LeaderEntry[]>('GET', '/leaderboard'),
};
